import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const FirmSettings = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [firm, setFirm] = useState({
    firmName: '',
    Address: '',
    ContactNo: '',
    Email: '',
    VatNo: '',
    CstNo: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [years, setYears] = useState([]);
  const [newStartYear, setNewStartYear] = useState('');
  const [newEndYear, setNewEndYear] = useState('');

  const fetchFirm = async () => {
    setLoading(true);
    try {
      const [firmRes, yearRes] = await Promise.all([
        axiosClient.get(`/settings/firms/${user?.firmId || 1}`),
        axiosClient.get('/settings/years')
      ]);

      if (firmRes.success) setFirm(firmRes.data || {});
      if (yearRes.success) setYears(yearRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirm();
  }, [user]);

  const handleFirmSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.put(`/settings/firms/${user?.firmId || 1}`, firm);
      showSuccess('Firm details updated successfully.');
    } catch (err) {
      showError(err.message || 'Error updating firm.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateYear = async (e) => {
    e.preventDefault();
    if (!newStartYear || !newEndYear) return;
    try {
      await axiosClient.post('/settings/years', {
        startYear: newStartYear,
        endYear: newEndYear
      });
      showSuccess('Financial Year added successfully.');
      setNewStartYear('');
      setNewEndYear('');
      fetchFirm();
    } catch (err) {
      showError(err.message || 'Error adding financial year.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-building-gear text-primary me-2"></i> Firm Profile & Settings
          </h3>
          <p className="text-muted small mb-0">Manage business information, branch contact info, and financial years</p>
        </div>
      </div>

      <div className="row g-4">
        {/* Firm Profile Form */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light py-2 fw-semibold">Firm / Company Details</div>
            <div className="card-body p-3">
              <form onSubmit={handleFirmSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Firm Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={firm.firmName || ''}
                    onChange={(e) => setFirm({ ...firm, firmName: e.target.value })}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Address</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={firm.Address || ''}
                    onChange={(e) => setFirm({ ...firm, Address: e.target.value })}
                  ></textarea>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Contact Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={firm.ContactNo || ''}
                      onChange={(e) => setFirm({ ...firm, ContactNo: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      value={firm.Email || ''}
                      onChange={(e) => setFirm({ ...firm, Email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="row g-2 mb-4">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">VAT / GST Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={firm.VatNo || ''}
                      onChange={(e) => setFirm({ ...firm, VatNo: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">CST / Reg Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={firm.CstNo || ''}
                      onChange={(e) => setFirm({ ...firm, CstNo: e.target.value })}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-ocean" disabled={saving}>
                  {saving ? 'Saving...' : 'Update Firm Details'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Financial Years List & Add */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2 fw-semibold">Add New Financial Year</div>
            <div className="card-body p-3">
              <form onSubmit={handleCreateYear} className="row g-2 align-items-end">
                <div className="col-6">
                  <label className="form-label small text-muted">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={newStartYear}
                    onChange={(e) => setNewStartYear(e.target.value)}
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small text-muted">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={newEndYear}
                    onChange={(e) => setNewEndYear(e.target.value)}
                    required
                  />
                </div>
                <div className="col-12 mt-2">
                  <button type="submit" className="btn btn-teal btn-sm w-100">
                    <i className="bi bi-plus-circle me-1"></i> Add Financial Year
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-2 fw-semibold">Available Financial Years</div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush small">
                {years.map((y) => {
                  const sY = new Date(y.StartYear).getFullYear();
                  const eY = new Date(y.EndYear).getFullYear();
                  const isActive = String(y.Fid) === String(user?.fid);
                  return (
                    <li key={y.Fid} className="list-group-item d-flex justify-content-between align-items-center py-2">
                      <div>
                        <strong>FY {sY}-{eY}</strong>
                        <div className="text-muted small">{formatDate(y.StartYear)} to {formatDate(y.EndYear)}</div>
                      </div>
                      {isActive && <span className="badge bg-success">Active Session</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmSettings;
